import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Get language from localStorage or default to English
    return localStorage.getItem('language') || 'en';
  });

  useEffect(() => {
    // Save language to localStorage whenever it changes
    localStorage.setItem('language', language);
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'fr' : 'en');
  };

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    t: (key) => translations[language]?.[key] || key
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Comprehensive translations object
const translations = {
  en: {
    // Login page
    welcome: 'Welcome',
    maintenanceAndRepairScheduling: 'Maintenance & Repair Scheduling',
    signIn: 'Sign In',
    signUp: 'Sign Up',
    username: 'Username',
    email: 'Email',
    password: 'Password',
    usernamePlaceholder: 'Enter your username',
    emailPlaceholder: 'Enter your email',
    passwordPlaceholder: 'Enter your password',
    loginButton: 'Sign In',
    registerButton: 'Create Account',
    forgotPassword: 'Forgot username or password?',
    signingIn: 'Signing In...',
    creatingAccount: 'Creating Account...',
    signInDescription: 'Sign in to manage your maintenance operations',
    start14DayTrial: 'Start your 14-day free trial today',
    role: 'Role',
    firstName: 'First Name',
    lastName: 'Last Name',
    companyCode: 'Company Code',
    
    // Dashboard Header
    dashboard: 'Maintenance and Repair Dashboard',
    dashboardSubtitle: 'Manage work orders and preventive maintenance',
    signOut: 'Sign Out',
    
    // Subscription Status
    freeTrial: 'Free Trial',
    daysRemaining: 'days remaining',
    expiresIn: 'Expires in',
    renewsIn: 'Renews in',
    daysUntilExpiry: 'days until expiry',
    enjoyFullAccess: 'Enjoy full access to all features during your trial period.',
    upgradeNow: 'Upgrade Now',
    upgradeToYearly: 'Upgrade to Yearly',
    renew: 'Renew',
    renewNow: 'Renew Now',
    managePlan: 'Manage Plan',
    renewSubscription: 'Renew Subscription',
    trialActive: 'Free Trial Active',
    trialExpired: 'Trial Expired',
    subscriptionExpired: 'Subscription Expired',
    subscriptionExpiresSoon: 'Your subscription expires soon. Renew to continue access.',
    upgradeForFullAccess: 'Upgrade to Professional for unlimited work orders, preventive maintenance, and parts inventory.',
    upgradeToYearlySave: 'Upgrade to yearly plan and save 5%!',
    fullAccessFeatures: 'You have full access to all professional features.',
    viewPlans: 'View Plans',
    activeSubscription: 'Active Subscription',
    renewToAccessDashboard: 'Please renew your subscription to access your dashboard',
    accessLocked: 'Dashboard Access Locked',
    subscriptionExpiredMessage: 'Your subscription has expired. Renew now to regain access to your maintenance tracking and all your data.',
    yourDataIsSafe: 'Your Data is Safe',
    dataPreservedMessage: 'All your work orders, maintenance records, and settings are preserved. They will be available immediately after renewal.',
    chooseMonthlyOrYearly: 'Choose monthly or yearly plan on the next page',
    
    // Profile Settings
    profileSettings: 'Profile Settings',
    fullName: 'Full Name',
    updateProfile: 'Update Profile',
    updating: 'Updating...',
    profileUpdated: 'Profile updated successfully',
    manageAccount: 'Manage your account',
    save: 'Save',
    saving: 'Saving...',
    cancel: 'Cancel',
    edit: 'Edit',
    addName: 'Add Name',
    noNameSet: 'No name set yet',
    noRoleSet: 'No role set yet',
    addRole: 'Add Role',
    
    // Calendar & Date
    months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    monthsShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    weekDays: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    weekDaysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    
    // History Pages
    filters: 'Filters',
    completedTasks: 'Completed Tasks',
    totalRecords: 'Total Records',
    daysOfHistory: 'Days of History',
    maintenanceTaskHistory: 'Maintenance Task History',
    repairTaskHistory: 'Repair Task History',
    taskTitle: 'Task Title',
    machine: 'Machine',
    department: 'Department',
    assignee: 'Assignee',
    completedDate: 'Completed Date',
    noRecordsFound: 'No records found',
    date: 'Date',
    workOrder: 'Work Order',
    itemTask: 'Item/Task',
    priority: 'Priority',
    type: 'Type',
    status: 'Status',
    completedBy: 'Completed By',
    actions: 'Actions',
    view: 'View',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
    urgent: 'Urgent',
    normal: 'Normal',
    corrective: 'Corrective',
    preventive: 'Preventive',
    inspection: 'Inspection',
    completed: 'Completed',
    inProgress: 'In Progress',
    pending: 'Pending',
    cancelled: 'Cancelled',
    
    // Calendar - Event Details
    scheduledTasksFor: 'Scheduled Tasks for',
    noTasksScheduled: 'No tasks scheduled for this day',
    scheduledStart: 'Scheduled Start',
    scheduledEnd: 'Scheduled End',
    dueDate: 'Due Date',
    createdDate: 'Created Date',
    description: 'Description',
    notes: 'Notes',
    
    // Task Cards (Common)
    title: 'Title',
    details: 'Details',
    createdOn: 'Created on',
    dueOn: 'Due on',
    scheduledFor: 'Scheduled for',
    noDescription: 'No description provided',
    noNotes: 'No notes added',
    selectMachine: 'Select Machine',
    selectDepartment: 'Select Department',
    selectAssignee: 'Select Assignee',
    selectPriority: 'Select Priority',
    selectType: 'Select Type',
    selectStatus: 'Select Status',
    
    // Task Card Actions
    markComplete: 'Mark Complete',
    markIncomplete: 'Mark Incomplete',
    delete: 'Delete',
    close: 'Close',
    addComment: 'Add Comment',
    attachments: 'Attachments',
    checklist: 'Checklist',
    checklistItems: 'Checklist Items',
    addItem: 'Add Item',
    photos: 'Photos',
    addPhoto: 'Add Photo',
    comments: 'Comments',
    
    // Form Fields - Repair Work Orders
    createRepairWorkOrder: 'Create Repair Work Order',
    addNewRepairWorkOrder: 'Add a new repair work order',
    repair: 'Repair',
    assigneeLabel: 'Assignee',
    selectAssigneeOptional: 'Select assignee (optional)',
    locationEquipment: 'Location & Equipment',
    site: 'Site',
    mainSite: 'Main Site',
    selectDepartmentOptional: 'Select department (optional)',
    selectDepartmentFirst: 'Select department first',
    specificLocation: 'Specific Location',
    specificLocationPlaceholder: 'e.g., Bay 3, Floor 2, Section A',
    scheduling: 'Scheduling',
    schedulingSection: 'Scheduling Section',
    departmentLabel: 'Department',
    machineLabel: 'Machine',
    selectDepartmentFirstMsg: 'Select department first',
    estimatedDuration: 'Estimated Duration (minutes)',
    estimatedDurationPlaceholder: 'e.g., 120',
    repairInstructions: 'Repair Instructions',
    repairInstructionsPlaceholder: 'Detailed instructions for the repair task...',
    safetyNotes: 'Safety Notes',
    safetyNotesPlaceholder: 'Safety precautions and requirements...',
    requiredParts: 'Required Parts & Tools',
    requiredPartsPlaceholder: 'List of parts, tools, or materials needed...',
    createWorkOrder: 'Create Work Order',
    creating: 'Creating...',
    
    // Form Fields - Maintenance Work Orders
    createDailyMaintenanceWorkOrder: 'Create Daily Maintenance Work Order',
    addNewPreventiveMaintenanceSchedule: 'Add a new preventive maintenance schedule',
    basicInformation: 'Basic Information',
    taskTitleRequired: 'Task Title *',
    enterWorkOrderTitle: 'Enter work order title',
    frequencyRequired: 'Frequency *',
    daily: 'Daily',
    weekly: 'Weekly',
    biweekly: 'Biweekly',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    yearly: 'Yearly',
    departmentRequired: 'Department *',
    selectDepartment: 'Select department',
    machineEquipmentRequired: 'Machine/Equipment *',
    selectMachineFirst: 'Select d\'abord le département',
    assignedLabel: 'Assigned',
    scheduledTime: 'Scheduled Time',
    priorityLabel: 'Priority',
    instructions: 'Instructions',
    maintenanceInstructions: 'Maintenance Instructions',
    maintenanceInstructionsPlaceholder: 'Detailed instructions for performing this maintenance task...',
    createTask: 'Create Task',
    cancel: 'Cancel',
    
    // Sidebar Menu
    repairWorkOrders: 'Repair Work Orders',
    repairWorkOrdersDesc: 'Repair work orders board',
    maintenanceWorkOrders: 'Maintenance Work Orders',
    maintenanceWorkOrdersDesc: 'Maintenance work orders board',
    calendar: 'Calendar',
    calendarDesc: 'View scheduled tasks',
    dailyTasks: 'Daily Tasks',
    dailyTasksDesc: 'Today\'s notifications',
    maintenanceHistory: 'Maintenance History',
    maintenanceHistoryDesc: 'View maintenance task history',
    repairHistory: 'Repair History',
    repairHistoryDesc: 'View repair task history',
    departments: 'Departments',
    departmentsDesc: 'Manage departments',
    machines: 'Machines',
    machinesDesc: 'Manage machines',
    assignees: 'Assignees',
    assigneesDesc: 'Manage technicians',
    partsInventory: 'Parts Inventory',
    partsInventoryDesc: 'Manage parts and supplies',
    support: 'Support',
    supportDesc: 'Help & user manual',
    
    // Support Page
    supportHelpCenter: 'Support & Help Center',
    supportDescription: 'User manual, quick tips, and developer support for Composys MaintPro',
    userManual: 'User Manual',
    quickTips: 'Quick Tips',
    contactDeveloper: 'Contact Developer',
    gettingStarted: 'Getting Started',
    welcomeToComposys: 'Welcome to Composys MaintPro - your complete maintenance and repair management solution.',
    afterLogging: 'After logging in, you\'ll see the main dashboard with access to all features.',
    your14DayTrial: 'Your 14-day free trial gives you full access to all premium features.',
    
    // Common Buttons & Actions
    addTask: 'Add Task',
    search: 'Search',
    searchWorkOrders: 'Search work orders...',
    allDepartments: 'All Departments',
    priority: 'Priority',
    status: 'Status',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    close: 'Close',
    create: 'Create',
    update: 'Update',
    print: 'Print',
    back: 'Back',
    
    // Work Order Form
    createRepairWorkOrder: 'Create Repair Work Order',
    addNewRepairWorkOrder: 'Add a new repair work order',
    createMaintenanceWorkOrder: 'Create Maintenance Work Order',
    basicInformation: 'Basic Information',
    title: 'Title',
    titleRequired: 'Title *',
    enterWorkOrderTitle: 'Enter work order title',
    type: 'Type',
    typeRequired: 'Type *',
    repair: 'Repair',
    assignee: 'Assignee',
    selectAssignee: 'Select assignee (optional)',
    locationEquipment: 'Location & Equipment',
    site: 'Site',
    mainSite: 'Main Site',
    department: 'Department',
    selectDepartment: 'Select department (optional)',
    machine: 'Machine',
    selectDepartmentFirst: 'Select department first',
    specificLocation: 'Specific Location',
    locationExample: 'e.g., Bay 3, Floor 2, Section A',
    scheduling: 'Scheduling',
    schedulingSection: 'Scheduling Section',
    departmentLabel: 'Department',
    machineLabel: 'Machine',
    selectDepartmentFirstMsg: 'Select department first',
    dueDate: 'Due Date',
    scheduledStart: 'Scheduled Start',
    scheduledEnd: 'Scheduled End',
    estimatedDuration: 'Estimated Duration (hours)',
    description: 'Description',
    repairInstructions: 'Repair Instructions',
    detailedDescription: 'Detailed description of the work to be performed...',
    detailedRepairInstructions: 'Detailed description of the repair work to be performed...',
    safetyNotes: 'Safety Notes',
    importantSafetyInfo: 'Important safety information and precautions...',
    checklist: 'Checklist',
    addChecklistItem: 'Add checklist item',
    checklistItemPlaceholder: 'Enter checklist item...',
    
    // Work Order Statuses
    scheduled: 'Scheduled',
    inProgress: 'In Progress',
    completed: 'Completed',
    onHold: 'On Hold',
    
    // Work Order Details
    workOrders: 'work orders',
    noWorkOrders: 'No work orders',
    workOrder: 'Work Order',
    
    // Maintenance Page
    dragDropTasks: 'Drag and drop tasks to change status',
    totalTasks: 'total tasks',
    dailyMaintenance: 'Daily Maintenance',
    dailyMaintenanceDesc: 'Daily inspection and maintenance tasks',
    weeklyMaintenance: 'Weekly Maintenance',
    weeklyMaintenanceDesc: 'Weekly preventive maintenance schedules',
    monthlyMaintenance: 'Monthly Maintenance',
    monthlyMaintenanceDesc: 'Monthly comprehensive maintenance',
    tasks: 'Tasks',
    noInProgressTasks: 'No in progress tasks',
    noCompletedTasks: 'No completed tasks',
    noOnHoldTasks: 'No on hold tasks',
    noScheduledTasks: 'No scheduled tasks',
    
    // Calendar
    calendarView: 'Calendar View',
    viewScheduledTasks: 'View scheduled tasks and maintenance activities',
    today: 'Today',
    selectDate: 'Select a date to view tasks',
    noTasksScheduled: 'No tasks scheduled for this date',
    
    // Daily Tasks
    dailyTasksPage: 'Daily Tasks',
    todayScheduledTasks: 'Today\'s scheduled tasks and activities',
    cleanupCompletedTasks: 'Cleanup Completed Tasks',
    
    // History Pages
    completedMaintenanceTasks: 'Completed Maintenance Tasks',
    viewHistoryMaintenance: 'View history of completed maintenance work',
    completedRepairTasks: 'Completed Repair Tasks',
    viewHistoryRepair: 'View history of completed repair work',
    completedOn: 'Completed on',
    completedBy: 'Completed by',
    deleteHistory: 'Delete History',
    taskName: 'Task Name',
    completionDate: 'Completion Date',
    noHistoryAvailable: 'No history available',
    
    // Department Management
    departmentManagement: 'Department Management',
    manageDepartments: 'Manage your organization\'s departments',
    addDepartment: 'Add Department',
    departmentName: 'Department Name',
    enterDepartmentName: 'Enter department name',
    addMachine: 'Add Machine',
    machineCount: 'machines',
    noDepartments: 'No departments found',
    createFirstDepartment: 'Create your first department to get started',
    
    // Machine Management
    machineManagement: 'Machine Management',
    manageMachines: 'Manage your organization\'s machines and equipment',
    addNewMachine: 'Add New Machine',
    machineName: 'Machine Name',
    enterMachineName: 'Enter machine name',
    model: 'Model',
    serialNumber: 'Serial Number',
    location: 'Location',
    installationDate: 'Installation Date',
    lastMaintenance: 'Last Maintenance',
    noMachines: 'No machines found',
    
    // Assignee Management
    assigneeManagement: 'Assignee Management',
    manageTechnicians: 'Manage technicians and maintenance staff',
    addAssignee: 'Add Assignee',
    name: 'Name',
    enterName: 'Enter name',
    phoneNumber: 'Phone Number',
    specialization: 'Specialization',
    selectSpecialization: 'Select specialization',
    electrical: 'Electrical',
    mechanical: 'Mechanical',
    plumbing: 'Plumbing',
    hvac: 'HVAC',
    general: 'General',
    noAssignees: 'No assignees found',
    
    // History Pages - Table Headers & Filters
    filters: 'Filters',
    search: 'Search',
    clearFilters: 'Clear Filters',
    allStatuses: 'All Statuses',
    allFrequencies: 'All Frequencies',
    allPriorities: 'All Priorities',
    completedItems: 'Completed Items',
    totalRecords: 'Total Records',
    daysOfHistory: 'Days of History',
    repairWorkOrderHistory: 'Repair Work Order History',
    maintenanceTaskHistory: 'Maintenance Task History',
    date: 'Date',
    task: 'Task',
    item: 'Item',
    itemTask: 'Item/Task',
    workOrder: 'Work Order',
    department: 'Department',
    machine: 'Machine',
    frequency: 'Frequency',
    type: 'Type',
    completedBy: 'Completed By',
    actions: 'Actions',
    delete: 'Delete',
    taskCompleted: 'Task Completed',
    workOrderCompleted: 'Work Order Completed',
    
    // Calendar Page
    calendarTitle: 'Calendar',
    viewScheduledMaintenance: 'View scheduled maintenance and repair work orders',
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday',
    
    // Create Maintenance Task Form
    createDailyMaintenanceTask: 'Create Daily Maintenance Task',
    addNewPreventiveMaintenance: 'Add a new preventive maintenance schedule',
    taskTitle: 'Task Title',
    taskTitleRequired: 'Task Title *',
    frequencyRequired: 'Frequency *',
    departmentRequired: 'Department *',
    selectDepartment: 'Select department',
    selectDepartmentOptional: 'Select department (optional)',
    machineEquipment: 'Machine/Equipment',
    machineEquipmentRequired: 'Machine/Equipment *',
    selectAssignee: 'Select assignee',
    selectAssigneeOptional: 'Select assignee (optional)',
    scheduledTime: 'Scheduled Time',
    instructions: 'Instructions',
    maintenanceInstructions: 'Maintenance Instructions',
    detailedInstructions: 'Detailed instructions for performing this maintenance task...',
    unassigned: 'Unassigned',
    selectMachine: 'Select machine',
    dayOfWeek: 'Day of Week',
    periodOfMonth: 'Period of Month',
    
    // Work Order Form Fields
    typeRequired: 'Type *',
    site: 'Site',
    mainSite: 'Main Site',
    specificLocation: 'Specific Location',
    locationExample: 'e.g., Bay 3, Floor 2, Section A',
    scheduling: 'Scheduling',
    schedulingSection: 'Scheduling Section',
    departmentLabel: 'Department',
    machineLabel: 'Machine',
    selectDepartmentFirstMsg: 'Select department first',
    daily: 'Daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
    
    // Support Page - Contact Developer
    contactDeveloperTab: 'Contact Developer',
    subject: 'Subject',
    subjectRequired: 'Subject *',
    briefDescription: 'Brief description of your issue or request',
    message: 'Message',
    messageRequired: 'Message *',
    describeIssue: 'Please describe your issue, question, or feedback in detail. Include steps to reproduce if reporting a bug.',
    sendSupportRequest: 'Send Support Request',
    developerInformation: 'Developer Information',
    responseTimes: 'Response Times',
    criticalIssues: 'Critical Issues:',
    within4Hours: 'Within 4 hours',
    bugReports: 'Bug Reports:',
    within24Hours: 'Within 24 hours',
    featureRequests: 'Feature Requests:',
    within48Hours: 'Within 48 hours',
    generalQuestions: 'General Questions:',
    within3BusinessDays: 'Within 3 business days',
    whatToInclude: 'What to Include',
    yourUsernameRole: 'Your username and role (Admin/Supervisor)',
    browserDeviceInfo: 'Browser and device information',
    stepsToReproduce: 'Steps to reproduce the issue',
    screenshotsApplicable: 'Screenshots if applicable',
    expectedVsActual: 'Expected vs. actual behavior',
    contactInformation: 'Contact Information',
    
    // Support Page - User Manual Content
    composysMaintProUserManual: 'Composys MaintPro User Manual',
    gettingStartedTitle: 'Getting Started',
    welcomeToComposysMaintPro: 'Welcome to Composys MaintPro - your complete maintenance and repair management solution.',
    afterLoggingIn: 'After logging in, you\'ll see the main dashboard with access to all features.',
    your14DayFreeTrial: 'Your 14-day free trial gives you full access to all premium features.',
    repairWorkOrdersTitle: 'Repair Work Orders',
    viewRepairWorkOrders: 'View repair work orders in a 4-column Kanban board: Scheduled → In Progress → Completed → On Hold',
    clickAnyWorkOrder: 'Click any work order card to view detailed information, checklists, and attachments',
    dragAndDropWorkOrders: 'Drag and drop work orders between columns to update their status',
    useSearchFilter: 'Use the search and filter options to find specific work orders',
    clickNewWorkOrder: 'Click the "New Work Order" button to create repair requests',
    editChecklistsBy: 'Edit checklists by clicking on work orders and adding/removing items',
    maintenanceWorkOrdersTitle: 'Maintenance Work Orders',
    schedulePreventive: 'Schedule preventive maintenance with Daily, Weekly, and Monthly frequencies',
    clickOnMaintenance: 'Click on maintenance type cards to view and create scheduled tasks',
    eachMaintenanceTask: 'Each maintenance task includes checklists, instructions, and safety notes',
    clickOnAnyMaintenance: 'Click on any maintenance task to edit details, checklists, and schedules',
    printMaintenanceWork: 'Print maintenance work orders for technicians in the field',
    trackCompletionProgress: 'Track completion progress with visual progress bars',
    
    // Support Page - Quick Tips
    quickTipsTitle: 'Quick Tips & Best Practices',
    useDragDrop: 'Use drag & drop to quickly update work order status',
    clickOnAnyCard: 'Click on any card throughout the system for detailed views',
    usePrintFunction: 'Use the print function to create field-ready work orders',
    setUpDepartments: 'Set up departments and machines first for better organization',
    checkDailyTasks: 'Check the Daily Tasks page every morning for today\'s priorities',
    proTipWorkflow: 'Pro Tip: Workflow Optimization',
    forMaximumEfficiency: 'For maximum efficiency, start each day by checking the Daily Tasks page, then use the Calendar view to plan your week. Create maintenance schedules during low-activity periods and always keep machine documentation updated for faster troubleshooting.',
    recommendedDailyWorkflow: 'Recommended daily workflow',
    
    // Priorities
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    critical: 'Critical',
    
    // Common
    language: 'Language',
    loading: 'Loading...',
    sending: 'Sending',
    noData: 'No data available',
    error: 'Error',
    success: 'Success',
    
    // Parts Inventory
    partsInventoryTitle: 'Parts Inventory',
    addPart: 'Add Part',
    partName: 'Part Name',
    partCode: 'Part Code',
    partType: 'Type',
    maintenance: 'Maintenance',
    repair: 'Repair',
    quantity: 'Quantity',
    minStockLevel: 'Minimum Stock Level',
    supplier: 'Supplier',
    supplierName: 'Supplier Name',
    supplierContact: 'Supplier Contact',
    supplierPhone: 'Phone',
    supplierEmail: 'Email',
    barcode: 'Barcode',
    barcodeType: 'Barcode Type',
    autoGenerate: 'Auto-generate',
    manual: 'Manual',
    notes: 'Notes',
    manuals: 'Manuals',
    quantityHistory: 'Quantity History',
    updateQuantity: 'Update Quantity',
    addStock: 'Add Stock',
    removeStock: 'Remove Stock',
    adjustStock: 'Adjust Stock',
    printLabel: 'Print Label',
    deletePart: 'Delete Part',
    editPart: 'Edit Part',
    savePart: 'Save Part',
    cancel: 'Cancel',
    createdAt: 'Created At',
    lowStock: 'Low Stock',
    inStock: 'In Stock',
    outOfStock: 'Out of Stock',
    partDetails: 'Part Details',
    barcodeQRCode: 'Barcode / QR Code',
    noParts: 'No parts found',
    createFirstPart: 'Create your first part',
    partCreatedSuccess: 'Part created successfully',
    partUpdatedSuccess: 'Part updated successfully',
    partDeletedSuccess: 'Part deleted successfully',
    quantityUpdatedSuccess: 'Quantity updated successfully',
    confirmDelete: 'Are you sure you want to delete this part?',
    professionalPlanRequired: 'Professional Plan Required',
    upgradeToAccessParts: 'Upgrade to Professional Plan to access Parts Inventory',
    searchParts: 'Search by part name or code...',
    printAllParts: 'Print All Parts',
    selectCodeType: 'Select code type for label',
    bothCodes: 'Both (Barcode + QR)',
    barcodeOnly: 'Barcode Only',
    qrOnly: 'QR Code Only',
    printSize: 'Label Size: 2" x 1" (Industry Standard)',
    selectPartsFilter: 'Select parts to print',
    allParts: 'All Parts (Repair + Maintenance)',
    maintenancePartsOnly: 'Maintenance Parts Only',
    repairPartsOnly: 'Repair Parts Only',
    
    // Kanban & Work Orders
    allPriorities: 'All Priorities',
    machineField: 'Machine',
    due: 'Due',
    start: 'Start',
    end: 'End',
    maintenance: 'Maintenance',
    noTasks: 'No tasks',
    taskId: 'Task ID',
    
    // History Pages Additional
    downloadCSV: 'Download CSV',
    maintenanceHistoryReport: 'Maintenance History Report',
    repairHistoryReport: 'Repair History Report',
    generated: 'Generated',
    user: 'User',
    summary: 'Summary',
    totalRecordsLabel: 'Total Records',
    completedTasksLabel: 'Completed Tasks',
    dateRangeLabel: 'Date Range',
    noData: 'No data',
    date: 'Date',
    taskTitle: 'Task Title',
    checklistItem: 'Checklist Item',
    item: 'Item',
    completedBy: 'Completed By',
    pending: 'Pending',
    overdue: 'Overdue',
    allStatuses: 'All Statuses',
    allFrequencies: 'All Frequencies',
    clearFilters: 'Clear Filters',
    completedTasks: 'Completed Tasks',
    totalRecords: 'Total Records',
    daysOfHistory: 'Days of History',
    maintenanceTaskHistory: 'Maintenance Task History',
    repairWorkOrderHistory: 'Repair Work Order History',
    recordsOf: 'of',
    noMaintenanceHistoryFound: 'No Maintenance History Found',
    noRepairHistoryFound: 'No Repair History Found',
    completeSomeTasks: 'Complete some maintenance tasks to see history here.',
    completeSomeRepairs: 'Complete some repairs to see history here.',
    tryAdjustingFilters: 'Try adjusting your search filters.',
    loadingMaintenanceHistory: 'Loading maintenance history...',
    loadingRepairHistory: 'Loading repair history...',
    maintenanceHistoryDownloaded: 'Maintenance history downloaded successfully!',
    repairHistoryDownloaded: 'Repair history downloaded successfully!',
    failedToDownloadHistory: 'Failed to download history',
    areYouSureDeleteHistory: 'Are you sure you want to delete this history entry?',
    historyEntryDeleted: 'History entry deleted successfully!',
    failedToDeleteHistory: 'Failed to delete history entry',
    workOrders: 'work orders',
    workOrder: 'Work Order',
    itemTask: 'Item/Task',
    assignee: 'Assignee',
    estimatedDuration: 'Est. Duration (hrs)',
    actualDuration: 'Actual Duration (hrs)',
    description: 'Description',
    completedWorkOrders: 'Completed Work Orders',
    completedItems: 'Completed Items',
    
    // ROI Calculator
    roiCalculatorTitle: 'See your CMMS savings in minutes',
    roiCalculatorSubtitle: 'Estimate how much Composys MaintPro can save your plant in time, downtime, and admin effort.',
    notSureYet: 'Not sure yet?',
    calculateROI: 'Calculate your ROI',
    calculateROIWith: 'Calculate your ROI with Composys MaintPro',
    noCreditCard: 'No credit card',
    fastSetup: 'Fast setup',
    builtForIndustrial: 'Built for industrial teams',
    yourCurrentOperation: 'Your Current Operation',
    enterTeamDetails: 'Enter your team and operation details',
    numberOfTechnicians: 'Number of technicians',
    workOrdersPerWeek: 'Work orders per week',
    plannerTimeWeekly: 'Planner time spent weekly on scheduling (hours)',
    avgTechnicianCost: 'Avg technician hourly cost (CAD)',
    estimatedDowntime: 'Estimated unplanned downtime hours per month',
    downtimeCostPerHour: 'Downtime cost per hour (CAD)',
    currentToolCost: 'Current tool cost per month (CAD)',
    leaveAtZero: 'Leave at 0 if using Excel or paper',
    composysMaintProPlan: 'Composys MaintPro plan',
    starterPlan: 'Starter',
    professionalPlan: 'Professional',
    assumptionsAdjustable: 'Assumptions (Adjustable)',
    fineTuneEstimates: 'Fine-tune these estimates based on your experience',
    timeSavedPlanning: 'Time saved on planning/coordination',
    downtimeReduction: 'Downtime reduction',
    adminTimeSaved: 'Admin time saved per technician (min/week)',
    estimatedMonthlySavings: 'Estimated Monthly Gross Savings',
    netSavingsAfterSub: 'Net Savings after Subscription',
    perMonth: 'per month',
    roi: 'ROI',
    paybackPeriod: 'Payback Period',
    days: 'days',
    savingsBreakdown: 'Savings Breakdown',
    plannerTimeSavings: 'Planner time savings',
    technicianAdminSavings: 'Technician admin savings',
    downtimeSavings: 'Downtime savings',
    start14DayTrial: 'Start 14-day free trial',
    shareEstimate: 'Share estimate',
    reset: 'Reset',
    composysFooter: '© Composys MaintPro — Estimates for guidance only.',
    calculate: 'Calculate',
    results: 'Results',
    monthlySavings: 'Monthly Savings',
    totalCost: 'Total Cost',
    efficiency: 'Efficiency',
    viewPlans: 'View Plans',
    exploreOurPricing: 'Explore our pricing',
    chooseYourPlan: 'Choose Your Plan',
    pricingSubtitle: 'Simple, transparent pricing for your maintenance management needs',
    backToDashboard: 'Back to Dashboard',
    backToLogin: 'Back to Login',
    freeTrial: 'Free Trial',
    daysRemaining: 'days remaining',
    activeSubscription: 'Active Subscription',
    newPlan: 'New Plan',
    upgradePlan: 'Upgrade Plan',
    monthly: 'Monthly',
    yearly: 'Yearly',
    mostPopular: 'Most Popular',
    perMonth: '/month',
    perYear: '/year',
    startPlan: 'Start',
    currentPlan: 'Current Plan',
    upgradingTo: 'Upgrading To',
    upgradeCalculation: 'Upgrade Calculation',
    amountDueToday: 'Amount Due Today',
    creditApplied: 'Credit Applied',
    prorated: 'Prorated',
    confirmUpgrade: 'Confirm Upgrade',
    starterPlan: 'Starter Plan',
    professionalPlan: 'Professional Plan',
    perfectForSmallTeams: 'Perfect for small teams',
    bestForGrowingBusinesses: 'Best for growing businesses',
    maintenanceWorkOrders: 'Maintenance work orders',
    repairWorkOrders: 'Repair work orders',
    maintenanceHistoryFeature: 'Maintenance history',
    repairHistoryFeature: 'Repair history',
    emailSupport: 'Email support',
    inventoryTracking: 'Inventory tracking',
    save: 'Save',
    allPlansInclude: 'All plans include:',
    dayFreeTrial: '14-day free trial',
    cancelAnytime: 'Cancel anytime',
    securePayments: 'Secure payments powered by Stripe',
    support247: '24/7 support',
    allPricesCAD: 'All prices are in CAD dollars. Taxes will be calculated at checkout based on your location.',
    upgradeNowFor: 'Upgrade Now for',
    processing: 'Processing...',
    daysRemainingCurrentPlan: 'Days Remaining on Current Plan',
    currentPlanValue: 'Current Plan Value (Remaining)',
    targetPlanValue: 'Target Plan Value (Remaining)',
    upgradeCostPayNow: 'Upgrade Cost (Pay Now)',
    upgradeNote: '* You only pay the difference for the remaining',
    upgradeNote2: 'days. Your subscription will expire on the same date as your current plan.',
    allPlansIncludeText: 'All plans include:',
    dayFreeTrialBullet: '14-day free trial',
    cancelAnytimeBullet: 'Cancel anytime',
    securePaymentsBullet: 'Secure payments powered by Stripe',
    support247Bullet: '24/7 support',
    pricesInCAD: 'All prices are in CAD dollars. Taxes will be calculated at checkout based on your location.',
    companyCode: 'Company Code',
    optional: 'optional',
    companyCodePlaceholder: 'Enter company code to access another company',
    companyCodeHint: 'Leave blank to access your own company dashboard',
    detailedMaintenanceInstructions: 'Detailed maintenance instructions...',
    addChecklistItem: 'Add checklist item (e.g., Check oil levels, Inspect belts)',
    edit: 'Edit',
    save: 'Save',
    saving: 'Saving...',
    print: 'Print',
    delete: 'Delete',
    details: 'Details',
    checklist: 'Checklist',
    files: 'Files',
    of: 'of',
    completedLower: 'completed',
    filesDocumentation: 'Files & Documentation',
    uploadFile: 'Upload File',
    noFilesUploaded: 'No files uploaded yet',
    uploadFilesDescription: 'Upload manuals, diagrams, or documentation for this repair work order',
    machineEquipment: 'Machine/Equipment',
    cmmsTagline: 'Easy CMMS for small to mid-size plants — track maintenance and repair ROI and reduce downtime.'
  },
  fr: {
    // Login page
    welcome: 'Bienvenue',
    maintenanceAndRepairScheduling: 'Planification de maintenance et de réparation',
    signIn: 'Se connecter',
    signUp: 'S\'inscrire',
    username: 'Nom d\'utilisateur',
    email: 'Email',
    password: 'Mot de passe',
    usernamePlaceholder: 'Entrez votre nom d\'utilisateur',
    emailPlaceholder: 'Entrez votre email',
    passwordPlaceholder: 'Entrez votre mot de passe',
    loginButton: 'Se connecter',
    registerButton: 'Créer un compte',
    forgotPassword: 'Nom d\'utilisateur ou mot de passe oublié?',
    signingIn: 'Connexion en cours...',
    creatingAccount: 'Création du compte...',
    signInDescription: 'Connectez-vous pour gérer vos opérations de maintenance',
    start14DayTrial: 'Commencez votre essai gratuit de 14 jours aujourd\'hui',
    role: 'Rôle',
    firstName: 'Prénom',
    lastName: 'Nom de famille',
    companyCode: 'Code d\'entreprise',
    
    // Dashboard Header
    dashboard: 'Tableau de bord de maintenance et de réparation',
    dashboardSubtitle: 'Gérer les bons de travail et la maintenance préventive',
    signOut: 'Se déconnecter',
    
    // Subscription Status
    freeTrial: 'Essai gratuit',
    daysRemaining: 'jours restants',
    expiresIn: 'Expire dans',
    renewsIn: 'Renouvelle dans',
    daysUntilExpiry: 'jours jusqu\'à l\'expiration',
    enjoyFullAccess: 'Profitez d\'un accès complet à toutes les fonctionnalités pendant votre période d\'essai.',
    upgradeNow: 'Mettre à niveau maintenant',
    upgradeToYearly: 'Passer à l\'annuel',
    renew: 'Renouveler',
    renewNow: 'Renouveler maintenant',
    managePlan: 'Gérer le forfait',
    renewSubscription: 'Renouveler l\'abonnement',
    trialActive: 'Essai gratuit actif',
    trialExpired: 'Essai expiré',
    subscriptionExpired: 'Abonnement expiré',
    subscriptionExpiresSoon: 'Votre abonnement expire bientôt. Renouvelez pour continuer l\'accès.',
    upgradeForFullAccess: 'Passez à Professionnel pour des bons de travail illimités, maintenance préventive et inventaire de pièces.',
    upgradeToYearlySave: 'Passez au plan annuel et économisez 5%!',
    fullAccessFeatures: 'Vous avez un accès complet à toutes les fonctionnalités professionnelles.',
    viewPlans: 'Voir les plans',
    activeSubscription: 'Abonnement actif',
    renewToAccessDashboard: 'Veuillez renouveler votre abonnement pour accéder à votre tableau de bord',
    accessLocked: 'Accès au tableau de bord verrouillé',
    subscriptionExpiredMessage: 'Votre abonnement a expiré. Renouvelez maintenant pour retrouver l\'accès à votre suivi de maintenance et toutes vos données.',
    yourDataIsSafe: 'Vos données sont en sécurité',
    dataPreservedMessage: 'Tous vos bons de travail, dossiers de maintenance et paramètres sont préservés. Ils seront disponibles immédiatement après le renouvellement.',
    chooseMonthlyOrYearly: 'Choisissez un plan mensuel ou annuel sur la page suivante',
    
    // Profile Settings
    profileSettings: 'Paramètres du profil',
    fullName: 'Nom complet',
    updateProfile: 'Mettre à jour le profil',
    updating: 'Mise à jour...',
    profileUpdated: 'Profil mis à jour avec succès',
    manageAccount: 'Gérer votre compte',
    save: 'Enregistrer',
    saving: 'Enregistrement...',
    cancel: 'Annuler',
    edit: 'Modifier',
    addName: 'Ajouter un nom',
    noNameSet: 'Aucun nom défini',
    noRoleSet: 'Aucun rôle défini',
    addRole: 'Ajouter un rôle',
    
    // Calendar & Date
    months: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
    monthsShort: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'],
    weekDays: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
    weekDaysShort: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
    
    // History Pages
    filters: 'Filtres',
    completedTasks: 'Tâches terminées',
    totalRecords: 'Total des enregistrements',
    daysOfHistory: 'Jours d\'historique',
    maintenanceTaskHistory: 'Historique des tâches de maintenance',
    repairTaskHistory: 'Historique des tâches de réparation',
    taskTitle: 'Titre de la tâche',
    machine: 'Machine',
    department: 'Département',
    assignee: 'Assigné',
    completedDate: 'Date d\'achèvement',
    noRecordsFound: 'Aucun enregistrement trouvé',
    date: 'Date',
    workOrder: 'Bon de travail',
    itemTask: 'Article/Tâche',
    priority: 'Priorité',
    type: 'Type',
    status: 'Statut',
    completedBy: 'Complété par',
    actions: 'Actions',
    view: 'Voir',
    high: 'Élevée',
    medium: 'Moyenne',
    low: 'Basse',
    urgent: 'Urgent',
    normal: 'Normal',
    corrective: 'Correctif',
    preventive: 'Préventif',
    inspection: 'Inspection',
    completed: 'Terminé',
    inProgress: 'En cours',
    pending: 'En attente',
    cancelled: 'Annulé',
    
    // Calendar - Event Details
    scheduledTasksFor: 'Tâches programmées pour',
    noTasksScheduled: 'Aucune tâche programmée pour ce jour',
    scheduledStart: 'Début prévu',
    scheduledEnd: 'Fin prévue',
    dueDate: 'Date d\'échéance',
    createdDate: 'Date de création',
    description: 'Description',
    notes: 'Notes',
    
    // Task Cards (Common)
    title: 'Titre',
    details: 'Détails',
    createdOn: 'Créé le',
    dueOn: 'Échéance le',
    scheduledFor: 'Programmé pour',
    noDescription: 'Aucune description fournie',
    noNotes: 'Aucune note ajoutée',
    selectMachine: 'Sélectionner une machine',
    selectDepartment: 'Sélectionner un département',
    selectAssignee: 'Sélectionner un assigné',
    selectPriority: 'Sélectionner une priorité',
    selectType: 'Sélectionner un type',
    selectStatus: 'Sélectionner un statut',
    
    // Task Card Actions
    markComplete: 'Marquer comme terminé',
    markIncomplete: 'Marquer comme incomplet',
    delete: 'Supprimer',
    close: 'Fermer',
    addComment: 'Ajouter un commentaire',
    attachments: 'Pièces jointes',
    checklist: 'Liste de contrôle',
    checklistItems: 'Éléments de la liste',
    addItem: 'Ajouter un élément',
    photos: 'Photos',
    addPhoto: 'Ajouter une photo',
    comments: 'Commentaires',
    
    // Sidebar Menu
    repairWorkOrders: 'Bons de travail de réparation',
    repairWorkOrdersDesc: 'Tableau des bons de réparation',
    maintenanceWorkOrders: 'Bons de travail de maintenance',
    maintenanceWorkOrdersDesc: 'Tableau des bons de maintenance',
    calendar: 'Calendrier',
    calendarDesc: 'Voir les tâches planifiées',
    dailyTasks: 'Tâches quotidiennes',
    dailyTasksDesc: 'Notifications d\'aujourd\'hui',
    maintenanceHistory: 'Historique de maintenance',
    maintenanceHistoryDesc: 'Voir l\'historique des tâches de maintenance',
    repairHistory: 'Historique de réparation',
    repairHistoryDesc: 'Voir l\'historique des tâches de réparation',
    departments: 'Départements',
    departmentsDesc: 'Gérer les départements',
    machines: 'Machines',
    machinesDesc: 'Gérer les machines',
    assignees: 'Assignés',
    assigneesDesc: 'Gérer les techniciens',
    partsInventory: 'Inventaire des pièces',
    partsInventoryDesc: 'Gérer les pièces et fournitures',
    support: 'Support',
    supportDesc: 'Aide et manuel d\'utilisation',
    
    // Support Page
    supportHelpCenter: 'Centre d\'aide et de support',
    supportDescription: 'Manuel d\'utilisation, conseils rapides et support développeur pour Composys MaintPro',
    userManual: 'Manuel d\'utilisation',
    quickTips: 'Conseils rapides',
    contactDeveloper: 'Contacter le développeur',
    gettingStarted: 'Commencer',
    welcomeToComposys: 'Bienvenue sur Composys MaintPro - votre solution complète de gestion de maintenance et de réparation.',
    afterLogging: 'Après vous être connecté, vous verrez le tableau de bord principal avec accès à toutes les fonctionnalités.',
    your14DayTrial: 'Votre essai gratuit de 14 jours vous donne un accès complet à toutes les fonctionnalités premium.',
    
    // Common Buttons & Actions
    addTask: 'Ajouter une tâche',
    search: 'Rechercher',
    searchWorkOrders: 'Rechercher des bons de travail...',
    allDepartments: 'Tous les départements',
    priority: 'Priorité',
    status: 'Statut',
    save: 'Enregistrer',
    cancel: 'Annuler',
    delete: 'Supprimer',
    edit: 'Modifier',
    close: 'Fermer',
    create: 'Créer',
    update: 'Mettre à jour',
    print: 'Imprimer',
    back: 'Retour',
    
    // Work Order Form
    createRepairWorkOrder: 'Créer un bon de réparation',
    addNewRepairWorkOrder: 'Ajouter un nouveau bon de réparation',
    createMaintenanceWorkOrder: 'Créer un bon de maintenance',
    basicInformation: 'Informations de base',
    title: 'Titre',
    titleRequired: 'Titre *',
    enterWorkOrderTitle: 'Entrez le titre du bon de travail',
    type: 'Type',
    typeRequired: 'Type *',
    repair: 'Réparation',
    assignee: 'Assigné',
    selectAssignee: 'Sélectionner l\'assigné (optionnel)',
    locationEquipment: 'Emplacement et équipement',
    site: 'Site',
    mainSite: 'Site principal',
    department: 'Département',
    selectDepartment: 'Sélectionner le département (optionnel)',
    machine: 'Machine',
    selectDepartmentFirst: 'Sélectionner d\'abord le département',
    specificLocation: 'Emplacement spécifique',
    locationExample: 'ex: Baie 3, Étage 2, Section A',
    scheduling: 'Planification',
    dueDate: 'Date d\'échéance',
    scheduledStart: 'Début planifié',
    scheduledEnd: 'Fin planifiée',
    estimatedDuration: 'Durée estimée (heures)',
    description: 'Description',
    repairInstructions: 'Instructions de réparation',
    detailedDescription: 'Description détaillée du travail à effectuer...',
    detailedRepairInstructions: 'Description détaillée du travail de réparation à effectuer...',
    safetyNotes: 'Notes de sécurité',
    importantSafetyInfo: 'Informations de sécurité importantes et précautions...',
    checklist: 'Liste de contrôle',
    addChecklistItem: 'Ajouter un élément',
    checklistItemPlaceholder: 'Entrez un élément de la liste de contrôle...',
    
    // Work Order Statuses
    scheduled: 'Planifié',
    inProgress: 'En cours',
    completed: 'Terminé',
    onHold: 'En attente',
    
    // Work Order Details
    workOrders: 'bons de travail',
    noWorkOrders: 'Aucun bon de travail',
    workOrder: 'Bon de travail',
    
    // Maintenance Page
    dragDropTasks: 'Glissez-déposez les tâches pour changer le statut',
    totalTasks: 'tâches au total',
    dailyMaintenance: 'Maintenance quotidienne',
    dailyMaintenanceDesc: 'Tâches d\'inspection et de maintenance quotidiennes',
    weeklyMaintenance: 'Maintenance hebdomadaire',
    weeklyMaintenanceDesc: 'Calendriers de maintenance préventive hebdomadaires',
    monthlyMaintenance: 'Maintenance mensuelle',
    monthlyMaintenanceDesc: 'Maintenance complète mensuelle',
    tasks: 'Tâches',
    noInProgressTasks: 'Aucune tâche en cours',
    noCompletedTasks: 'Aucune tâche terminée',
    noOnHoldTasks: 'Aucune tâche en attente',
    noScheduledTasks: 'Aucune tâche planifiée',
    
    // Calendar
    calendarView: 'Vue calendrier',
    calendarTitle: 'Calendrier',
    viewScheduledMaintenance: 'Voir la maintenance et les bons de travail de réparation planifiés',
    viewScheduledTasks: 'Voir les tâches planifiées et les activités de maintenance',
    today: 'Aujourd\'hui',
    selectDate: 'Sélectionnez une date pour voir les tâches',
    noTasksScheduled: 'Aucune tâche planifiée pour cette date',
    monday: 'Lundi',
    tuesday: 'Mardi',
    wednesday: 'Mercredi',
    thursday: 'Jeudi',
    friday: 'Vendredi',
    saturday: 'Samedi',
    sunday: 'Dimanche',
    
    // Daily Tasks
    dailyTasksPage: 'Tâches quotidiennes',
    todayScheduledTasks: 'Tâches et activités planifiées pour aujourd\'hui',
    cleanupCompletedTasks: 'Nettoyer les tâches terminées',
    
    // History Pages
    completedMaintenanceTasks: 'Tâches de maintenance terminées',
    viewHistoryMaintenance: 'Voir l\'historique des travaux de maintenance terminés',
    completedRepairTasks: 'Tâches de réparation terminées',
    viewHistoryRepair: 'Voir l\'historique des travaux de réparation terminés',
    completedOn: 'Terminé le',
    completedBy: 'Terminé par',
    deleteHistory: 'Supprimer l\'historique',
    taskName: 'Nom de la tâche',
    completionDate: 'Date d\'achèvement',
    noHistoryAvailable: 'Aucun historique disponible',
    
    // History Pages - Table Headers & Filters
    filters: 'Filtres',
    search: 'Rechercher',
    clearFilters: 'Effacer les filtres',
    allStatuses: 'Tous les statuts',
    allFrequencies: 'Toutes les fréquences',
    allPriorities: 'Toutes les priorités',
    completedItems: 'Éléments terminés',
    totalRecords: 'Total des enregistrements',
    daysOfHistory: 'Jours d\'historique',
    repairWorkOrderHistory: 'Historique des bons de réparation',
    maintenanceTaskHistory: 'Historique des tâches de maintenance',
    date: 'Date',
    task: 'Tâche',
    item: 'Élément',
    itemTask: 'Élément/Tâche',
    workOrder: 'Bon de travail',
    department: 'Département',
    machine: 'Machine',
    frequency: 'Fréquence',
    type: 'Type',
    actions: 'Actions',
    delete: 'Supprimer',
    taskCompleted: 'Tâche terminée',
    workOrderCompleted: 'Bon de travail terminé',
    
    // Create Maintenance Task Form
    createDailyMaintenanceTask: 'Créer une tâche de maintenance quotidienne',
    addNewPreventiveMaintenance: 'Ajouter un nouveau calendrier de maintenance préventive',
    taskTitle: 'Titre de la tâche',
    taskTitleRequired: 'Titre de la tâche *',
    frequencyRequired: 'Fréquence *',
    departmentRequired: 'Département *',
    selectDepartment: 'Sélectionner le département',
    selectDepartmentOptional: 'Sélectionner le département (optionnel)',
    machineEquipment: 'Machine/Équipement',
    machineEquipmentRequired: 'Machine/Équipement *',
    selectAssignee: 'Sélectionner l\'assigné',
    selectAssigneeOptional: 'Sélectionner l\'assigné (optionnel)',
    scheduledTime: 'Heure planifiée',
    instructions: 'Instructions',
    maintenanceInstructions: 'Instructions de maintenance',
    detailedInstructions: 'Instructions détaillées pour effectuer cette tâche de maintenance...',
    unassigned: 'Non assigné',
    selectMachine: 'Sélectionner la machine',
    dayOfWeek: 'Jour de la semaine',
    periodOfMonth: 'Période du mois',
    
    // Work Order Form Fields
    typeRequired: 'Type *',
    site: 'Site',
    mainSite: 'Site principal',
    specificLocation: 'Emplacement spécifique',
    locationExample: 'ex: Baie 3, Étage 2, Section A',
    scheduling: 'Planification',
    daily: 'Quotidien',
    weekly: 'Hebdomadaire',
    monthly: 'Mensuel',
    
    // Department Management
    departmentManagement: 'Gestion des départements',
    manageDepartments: 'Gérer les départements de votre organisation',
    addDepartment: 'Ajouter un département',
    departmentName: 'Nom du département',
    enterDepartmentName: 'Entrez le nom du département',
    addMachine: 'Ajouter une machine',
    machineCount: 'machines',
    noDepartments: 'Aucun département trouvé',
    createFirstDepartment: 'Créez votre premier département pour commencer',
    
    // Machine Management
    machineManagement: 'Gestion des machines',
    manageMachines: 'Gérer les machines et équipements de votre organisation',
    addNewMachine: 'Ajouter une nouvelle machine',
    machineName: 'Nom de la machine',
    enterMachineName: 'Entrez le nom de la machine',
    model: 'Modèle',
    serialNumber: 'Numéro de série',
    location: 'Emplacement',
    installationDate: 'Date d\'installation',
    lastMaintenance: 'Dernière maintenance',
    noMachines: 'Aucune machine trouvée',
    
    // Assignee Management
    assigneeManagement: 'Gestion des assignés',
    manageTechnicians: 'Gérer les techniciens et le personnel de maintenance',
    addAssignee: 'Ajouter un assigné',
    name: 'Nom',
    enterName: 'Entrez le nom',
    phoneNumber: 'Numéro de téléphone',
    specialization: 'Spécialisation',
    selectSpecialization: 'Sélectionner la spécialisation',
    electrical: 'Électrique',
    mechanical: 'Mécanique',
    plumbing: 'Plomberie',
    hvac: 'CVC',
    general: 'Général',
    noAssignees: 'Aucun assigné trouvé',
    
    // Support Page - Contact Developer
    contactDeveloperTab: 'Contacter le développeur',
    subject: 'Sujet',
    subjectRequired: 'Sujet *',
    briefDescription: 'Brève description de votre problème ou demande',
    message: 'Message',
    messageRequired: 'Message *',
    describeIssue: 'Veuillez décrire votre problème, question ou commentaire en détail. Incluez les étapes pour reproduire si vous signalez un bug.',
    sendSupportRequest: 'Envoyer la demande de support',
    developerInformation: 'Informations sur le développeur',
    responseTimes: 'Délais de réponse',
    criticalIssues: 'Problèmes critiques:',
    within4Hours: 'Dans les 4 heures',
    bugReports: 'Rapports de bugs:',
    within24Hours: 'Dans les 24 heures',
    featureRequests: 'Demandes de fonctionnalités:',
    within48Hours: 'Dans les 48 heures',
    generalQuestions: 'Questions générales:',
    within3BusinessDays: 'Dans les 3 jours ouvrables',
    whatToInclude: 'Quoi inclure',
    yourUsernameRole: 'Votre nom d\'utilisateur et rôle (Admin/Superviseur)',
    browserDeviceInfo: 'Informations sur le navigateur et l\'appareil',
    stepsToReproduce: 'Étapes pour reproduire le problème',
    screenshotsApplicable: 'Captures d\'écran si applicable',
    expectedVsActual: 'Comportement attendu vs réel',
    contactInformation: 'Informations de contact',
    
    // Support Page - User Manual Content
    composysMaintProUserManual: 'Manuel d\'utilisation Composys MaintPro',
    gettingStartedTitle: 'Commencer',
    welcomeToComposysMaintPro: 'Bienvenue sur Composys MaintPro - votre solution complète de gestion de maintenance et de réparation.',
    afterLoggingIn: 'Après vous être connecté, vous verrez le tableau de bord principal avec accès à toutes les fonctionnalités.',
    your14DayFreeTrial: 'Votre essai gratuit de 14 jours vous donne un accès complet à toutes les fonctionnalités premium.',
    repairWorkOrdersTitle: 'Bons de travail de réparation',
    viewRepairWorkOrders: 'Voir les bons de réparation dans un tableau Kanban à 4 colonnes: Planifié → En cours → Terminé → En attente',
    clickAnyWorkOrder: 'Cliquez sur n\'importe quelle carte de bon de travail pour voir les informations détaillées, les listes de contrôle et les pièces jointes',
    dragAndDropWorkOrders: 'Glissez-déposez les bons de travail entre les colonnes pour mettre à jour leur statut',
    useSearchFilter: 'Utilisez les options de recherche et de filtre pour trouver des bons de travail spécifiques',
    clickNewWorkOrder: 'Cliquez sur le bouton "Nouveau bon de travail" pour créer des demandes de réparation',
    editChecklistsBy: 'Modifiez les listes de contrôle en cliquant sur les bons de travail et en ajoutant/supprimant des éléments',
    maintenanceWorkOrdersTitle: 'Bons de travail de maintenance',
    schedulePreventive: 'Planifiez la maintenance préventive avec des fréquences quotidiennes, hebdomadaires et mensuelles',
    clickOnMaintenance: 'Cliquez sur les cartes de type de maintenance pour voir et créer des tâches planifiées',
    eachMaintenanceTask: 'Chaque tâche de maintenance comprend des listes de contrôle, des instructions et des notes de sécurité',
    clickOnAnyMaintenance: 'Cliquez sur n\'importe quelle tâche de maintenance pour modifier les détails, les listes de contrôle et les horaires',
    printMaintenanceWork: 'Imprimez les bons de travail de maintenance pour les techniciens sur le terrain',
    trackCompletionProgress: 'Suivez la progression de l\'achèvement avec des barres de progression visuelles',
    
    // Support Page - Quick Tips
    quickTipsTitle: 'Conseils rapides et meilleures pratiques',
    useDragDrop: 'Utilisez le glisser-déposer pour mettre à jour rapidement le statut des bons de travail',
    clickOnAnyCard: 'Cliquez sur n\'importe quelle carte du système pour des vues détaillées',
    usePrintFunction: 'Utilisez la fonction d\'impression pour créer des bons de travail prêts pour le terrain',
    setUpDepartments: 'Configurez d\'abord les départements et les machines pour une meilleure organisation',
    checkDailyTasks: 'Consultez la page Tâches quotidiennes chaque matin pour les priorités d\'aujourd\'hui',
    proTipWorkflow: 'Conseil pro: Optimisation du flux de travail',
    forMaximumEfficiency: 'Pour une efficacité maximale, commencez chaque jour en vérifiant la page Tâches quotidiennes, puis utilisez la vue Calendrier pour planifier votre semaine. Créez des calendriers de maintenance pendant les périodes de faible activité et gardez toujours la documentation des machines à jour pour un dépannage plus rapide.',
    recommendedDailyWorkflow: 'Flux de travail quotidien recommandé',
    
    // Priorities
    low: 'Faible',
    medium: 'Moyen',
    high: 'Élevé',
    critical: 'Critique',
    
    // Common
    language: 'Langue',
    loading: 'Chargement...',
    sending: 'Envoi',
    noData: 'Aucune donnée disponible',
    error: 'Erreur',
    success: 'Succès',
    
    // Parts Inventory
    partsInventoryTitle: 'Inventaire des pièces',
    addPart: 'Ajouter une pièce',
    partName: 'Nom de la pièce',
    partCode: 'Code de la pièce',
    partType: 'Type',
    maintenance: 'Maintenance',
    repair: 'Réparation',
    quantity: 'Quantité',
    minStockLevel: 'Niveau de stock minimum',
    supplier: 'Fournisseur',
    supplierName: 'Nom du fournisseur',
    supplierContact: 'Contact du fournisseur',
    supplierPhone: 'Téléphone',
    supplierEmail: 'E-mail',
    barcode: 'Code-barres',
    barcodeType: 'Type de code-barres',
    autoGenerate: 'Génération automatique',
    manual: 'Manuel',
    notes: 'Notes',
    manuals: 'Manuels',
    quantityHistory: 'Historique des quantités',
    updateQuantity: 'Mettre à jour la quantité',
    addStock: 'Ajouter du stock',
    removeStock: 'Retirer du stock',
    adjustStock: 'Ajuster le stock',
    printLabel: 'Imprimer l\'étiquette',
    deletePart: 'Supprimer la pièce',
    editPart: 'Modifier la pièce',
    savePart: 'Enregistrer la pièce',
    cancel: 'Annuler',
    createdAt: 'Créé le',
    lowStock: 'Stock bas',
    inStock: 'En stock',
    outOfStock: 'En rupture de stock',
    partDetails: 'Détails de la pièce',
    barcodeQRCode: 'Code-barres / QR Code',
    noParts: 'Aucune pièce trouvée',
    createFirstPart: 'Créer votre première pièce',
    partCreatedSuccess: 'Pièce créée avec succès',
    partUpdatedSuccess: 'Pièce mise à jour avec succès',
    partDeletedSuccess: 'Pièce supprimée avec succès',
    quantityUpdatedSuccess: 'Quantité mise à jour avec succès',
    confirmDelete: 'Êtes-vous sûr de vouloir supprimer cette pièce ?',
    professionalPlanRequired: 'Plan Professionnel requis',
    upgradeToAccessParts: 'Passez au Plan Professionnel pour accéder à l\'Inventaire des pièces',
    searchParts: 'Rechercher par nom ou code...',
    printAllParts: 'Imprimer toutes les pièces',
    selectCodeType: 'Sélectionner le type de code pour l\'étiquette',
    bothCodes: 'Les deux (Code-barres + QR)',
    barcodeOnly: 'Code-barres uniquement',
    qrOnly: 'QR Code uniquement',
    printSize: 'Taille d\'étiquette: 2" x 1" (Standard industriel)',
    selectPartsFilter: 'Sélectionner les pièces à imprimer',
    allParts: 'Toutes les pièces (Réparation + Maintenance)',
    maintenancePartsOnly: 'Pièces de maintenance uniquement',
    repairPartsOnly: 'Pièces de réparation uniquement',
    
    // Form Common Fields - French
    typeRequired: 'Type *',
    typeLabel: 'Type',
    preventiveMaintenance: 'Maintenance préventive',
    repair: 'Réparation',
    priorityRequired: 'Priorité *',
    priorityLow: 'Faible',
    priorityMedium: 'Moyen',
    priorityHigh: 'Élevé',
    priorityCritical: 'Critique',
    assigneeLabel: 'Assigné',
    selectAssigneeOptional: 'Sélectionner l\'assigné (optionnel)',
    noAssignee: 'Aucun assigné',
    
    // Location & Equipment - French
    locationEquipment: 'Emplacement et Équipement',
    site: 'Site',
    siteLabel: 'Site',
    departmentLabel: 'Département',
    selectDepartmentOptional: 'Sélectionner le département (optionnel)',
    noDepartment: 'Aucun département',
    machineLabel: 'Machine',
    selectMachine: 'Sélectionner la machine',
    selectDepartmentFirstMsg: 'Sélectionner d\'abord le département',
    noSpecificMachine: 'Aucune machine spécifique',
    specificLocation: 'Emplacement Spécifique',
    specificLocationPlaceholder: 'par ex., Baie 3, Étage 2, Section A',
    
    // Scheduling Section - French
    schedulingSection: 'Planification',
    dueDate: 'Date d\'échéance',
    estimatedDuration: 'Durée estimée (minutes)',
    estimatedDurationPlaceholder: 'par ex., 120',
    scheduledStart: 'Début Prévu',
    scheduledEnd: 'Fin Prévue',
    scheduledTime: 'Heure Prévue',
    minutes: 'minutes',
    notSet: 'Non défini',
    
    // Instructions & Notes - French
    repairInstructions: 'Instructions de Réparation',
    repairInstructionsPlaceholder: 'Description détaillée des travaux de réparation à effectuer...',
    maintenanceInstructions: 'Instructions de Maintenance',
    maintenanceInstructionsPlaceholder: 'Instructions détaillées pour effectuer cette tâche de maintenance...',
    safetyNotes: 'Notes de Sécurité',
    safetyNotesPlaceholder: 'Informations de sécurité importantes et précautions...',
    importantSafetyPrecautions: 'Considérations de sécurité importantes et précautions...',
    noInstructionsProvided: 'Aucune instruction fournie.',
    noRepairInstructions: 'Aucune instruction de réparation fournie.',
    noSafetyNotes: 'Aucune note de sécurité spécifiée.',
    detailedMaintenanceInstructions: 'Instructions de maintenance détaillées...',
    
    // Checklist - French
    checklistItemsSection: 'Éléments de Liste de Contrôle',
    addChecklistItem: 'Ajouter un élément de liste de contrôle...',
    addChecklistItemPlaceholder: 'Ajouter un nouvel élément (par ex., Vérifier les niveaux d\'huile, Inspecter les courroies)',
    addChecklistItemExample: 'Ajouter un élément (par ex., Vérifier les niveaux d\'huile)',
    noChecklistItems: 'Aucun élément de liste de contrôle pour ce bon de travail. Ajoutez des procédures de maintenance ci-dessus.',
    noChecklistItemsYet: 'Aucun élément de liste de contrôle pour le moment. Ajoutez des procédures de maintenance ci-dessus.',
    maintenanceChecklist: 'Liste de Contrôle de Maintenance',
    completed: 'terminé',
    completedBy: 'Terminé par',
    addedBy: 'Ajouté par',
    
    // Tags - French
    tagsSection: 'Étiquettes',
    addTag: 'Ajouter une étiquette...',
    
    // Buttons & Actions - French
    cancel: 'Annuler',
    creating: 'Création en cours...',
    createWorkOrder: 'Créer Bon de Travail',
    createTask: 'Créer Tâche',
    edit: 'Modifier',
    saving: 'Enregistrement...',
    save: 'Enregistrer',
    print: 'Imprimer',
    delete: 'Supprimer',
    refresh: 'Actualiser',
    uploadFile: 'Télécharger Fichier',
    
    // Status & Priority Colors - French
    statusScheduled: 'Planifié',
    statusInProgress: 'En Cours',
    statusCompleted: 'Terminé',
    statusOnHold: 'En Attente',
    overdue: 'En Retard',
    
    // Details Views - French
    details: 'Détails',
    documentation: 'Documentation',
    filesAndDiagrams: 'Fichiers et Diagrammes',
    maintenanceHistory: 'Historique de Maintenance',
    machineNotes: 'Notes sur la Machine',
    noNotesAdded: 'Aucune note ajoutée pour le moment.',
    addNotesAboutMachine: 'Ajouter des notes sur cette machine (procédures d\'exploitation, notes de sécurité, etc.)',
    specifications: 'Spécifications',
    noSpecificationsAdded: 'Aucune spécification ajoutée pour le moment.',
    technicalSpecifications: 'Spécifications techniques, numéros de modèle, capacité, etc.',
    locationDetails: 'Détails de l\'Emplacement',
    locationNotSpecified: 'Emplacement non spécifié.',
    specificLocationWithinDept: 'Emplacement spécifique dans le département (par ex., Baie 3, Ligne A, Étage 2)',
    machineId: 'ID de la Machine',
    created: 'Créé',
    machineDocumentation: 'Documentation de la Machine',
    uploadManuals: 'Télécharger des manuels, spécifications et guides de maintenance.',
    noFilesUploaded: 'Aucun fichier téléchargé pour le moment',
    uploadPDFs: 'Télécharger des PDFs, diagrammes, manuels ou photos liés à cette machine',
    maintenanceHistoryTracking: 'Le suivi de l\'historique de maintenance sera implémenté avec la fonctionnalité Bons de Travail de Maintenance.',
    filesDocumentation: 'Fichiers et Documentation',
    uploadManualsOrDocs: 'Télécharger des manuels, diagrammes ou documentation pour cette tâche de maintenance',
    
    // Work Order Detail - French
    requestedBy: 'Demandé par',
    location: 'Emplacement',
    selectAssignee: 'Sélectionner l\'assigné',
    notAssigned: 'Non assigné',
    filesAttachments: 'Fichiers et Pièces Jointes',
    fileManagement: 'La gestion des fichiers sera implémentée dans la Phase 2.',
    areYouSureDelete: 'Êtes-vous sûr de vouloir supprimer ce bon de travail ? Cette action ne peut pas être annulée.',
    areYouSureDeleteMaintenance: 'Êtes-vous sûr de vouloir supprimer cette tâche de maintenance ? Cette action ne peut pas être annulée.',
    
    // Frequency & Scheduling - French
    frequencyRequired: 'Fréquence *',
    daily: 'Quotidien',
    weekly: 'Hebdomadaire',
    monthly: 'Mensuel',
    dayOfWeek: 'Jour de la Semaine',
    monday: 'Lundi',
    tuesday: 'Mardi',
    wednesday: 'Mercredi',
    thursday: 'Jeudi',
    friday: 'Vendredi',
    saturday: 'Samedi',
    sunday: 'Dimanche',
    periodOfMonth: 'Période du Mois',
    startOfMonth: 'Début du Mois (Lun-Ven)',
    middleOfMonth: 'Milieu du Mois (Lun-Ven)',
    endOfMonth: 'Fin du Mois (Lun-Ven)',
    
    // Department Management - French
    noDepartmentsYet: 'Aucun Département Pour le Moment',
    createFirstDepartment: 'Créez votre premier département pour commencer à organiser votre installation. Les départements vous aident à catégoriser les bons de travail et les machines.',
    machines: 'Machines',
    idLabel: 'ID',
    
    // Kanban & Work Orders - French
    allPriorities: 'Toutes les Priorités',
    machineField: 'Machine',
    due: 'Échéance',
    start: 'Début',
    end: 'Fin',
    maintenance: 'Maintenance',
    noTasks: 'Aucune tâche',
    taskId: 'ID de Tâche',
    
    // History Pages Additional - French
    downloadCSV: 'Télécharger CSV',
    maintenanceHistoryReport: 'Rapport d\'Historique de Maintenance',
    repairHistoryReport: 'Rapport d\'Historique de Réparation',
    generated: 'Généré',
    user: 'Utilisateur',
    summary: 'Résumé',
    totalRecordsLabel: 'Enregistrements Totaux',
    completedTasksLabel: 'Tâches Terminées',
    dateRangeLabel: 'Plage de Dates',
    noData: 'Aucune donnée',
    date: 'Date',
    taskTitle: 'Titre de la Tâche',
    checklistItem: 'Élément de Liste',
    item: 'Élément',
    completedBy: 'Terminé par',
    pending: 'En Attente',
    overdue: 'En Retard',
    allStatuses: 'Tous les Statuts',
    allFrequencies: 'Toutes les Fréquences',
    clearFilters: 'Effacer les Filtres',
    completedTasks: 'Tâches Terminées',
    totalRecords: 'Enregistrements Totaux',
    daysOfHistory: 'Jours d\'Historique',
    maintenanceTaskHistory: 'Historique des Tâches de Maintenance',
    repairWorkOrderHistory: 'Historique des Bons de Travail de Réparation',
    recordsOf: 'enregistrements sur',
    noMaintenanceHistoryFound: 'Aucun Historique de Maintenance Trouvé',
    noRepairHistoryFound: 'Aucun Historique de Réparation Trouvé',
    completeSomeTasks: 'Terminez des tâches de maintenance pour voir l\'historique ici.',
    completeSomeRepairs: 'Terminez des réparations pour voir l\'historique ici.',
    tryAdjustingFilters: 'Essayez d\'ajuster vos filtres de recherche.',
    loadingMaintenanceHistory: 'Chargement de l\'historique de maintenance...',
    loadingRepairHistory: 'Chargement de l\'historique de réparation...',
    maintenanceHistoryDownloaded: 'Historique de maintenance téléchargé avec succès!',
    repairHistoryDownloaded: 'Historique de réparation téléchargé avec succès!',
    failedToDownloadHistory: 'Échec du téléchargement de l\'historique',
    areYouSureDeleteHistory: 'Êtes-vous sûr de vouloir supprimer cet enregistrement d\'historique?',
    historyEntryDeleted: 'Entrée d\'historique supprimée avec succès!',
    failedToDeleteHistory: 'Échec de la suppression de l\'entrée d\'historique',
    workOrders: 'bons de travail',
    workOrder: 'Bon de Travail',
    itemTask: 'Article/Tâche',
    assignee: 'Assigné',
    estimatedDuration: 'Durée Est. (hrs)',
    actualDuration: 'Durée Réelle (hrs)',
    description: 'Description',
    completedWorkOrders: 'Bons de Travail Terminés',
    completedItems: 'Éléments Terminés',
    
    // ROI Calculator - French
    roiCalculatorTitle: 'Calculez vos économies GMAO en quelques minutes',
    roiCalculatorSubtitle: 'Estimez combien Composys MaintPro peut faire économiser à votre usine en temps, temps d\'arrêt et effort administratif.',
    notSureYet: 'Pas encore sûr?',
    calculateROI: 'Calculez votre ROI',
    calculateROIWith: 'Calculez votre ROI avec Composys MaintPro',
    noCreditCard: 'Aucune carte de crédit',
    fastSetup: 'Configuration rapide',
    builtForIndustrial: 'Conçu pour les équipes industrielles',
    yourCurrentOperation: 'Votre Opération Actuelle',
    enterTeamDetails: 'Entrez les détails de votre équipe et de vos opérations',
    numberOfTechnicians: 'Nombre de techniciens',
    workOrdersPerWeek: 'Bons de travail par semaine',
    plannerTimeWeekly: 'Temps du planificateur passé chaque semaine sur la planification (heures)',
    avgTechnicianCost: 'Coût horaire moyen du technicien (CAD)',
    estimatedDowntime: 'Heures d\'arrêt imprévues estimées par mois',
    downtimeCostPerHour: 'Coût du temps d\'arrêt par heure (CAD)',
    currentToolCost: 'Coût de l\'outil actuel par mois (CAD)',
    leaveAtZero: 'Laisser à 0 si vous utilisez Excel ou papier',
    composysMaintProPlan: 'Plan Composys MaintPro',
    starterPlan: 'Démarrage',
    professionalPlan: 'Professionnel',
    assumptionsAdjustable: 'Hypothèses (Ajustables)',
    fineTuneEstimates: 'Affinez ces estimations en fonction de votre expérience',
    timeSavedPlanning: 'Temps économisé sur la planification/coordination',
    downtimeReduction: 'Réduction du temps d\'arrêt',
    adminTimeSaved: 'Temps administratif économisé par technicien (min/semaine)',
    estimatedMonthlySavings: 'Économies Mensuelles Brutes Estimées',
    netSavingsAfterSub: 'Économies Nettes après Abonnement',
    perMonth: 'par mois',
    roi: 'ROI',
    paybackPeriod: 'Période de Remboursement',
    days: 'jours',
    savingsBreakdown: 'Ventilation des Économies',
    plannerTimeSavings: 'Économies de temps du planificateur',
    technicianAdminSavings: 'Économies administratives des techniciens',
    downtimeSavings: 'Économies de temps d\'arrêt',
    start14DayTrial: 'Commencer l\'essai gratuit de 14 jours',
    shareEstimate: 'Partager l\'estimation',
    reset: 'Réinitialiser',
    composysFooter: '© Composys MaintPro — Estimations à titre indicatif uniquement.',
    calculate: 'Calculer',
    results: 'Résultats',
    monthlySavings: 'Économies Mensuelles',
    totalCost: 'Coût Total',
    efficiency: 'Efficacité',
    viewPlans: 'Voir les Plans',
    exploreOurPricing: 'Explorez nos tarifs',
    chooseYourPlan: 'Choisissez Votre Plan',
    pricingSubtitle: 'Tarification simple et transparente pour vos besoins de gestion de maintenance',
    backToDashboard: 'Retour au Tableau de Bord',
    backToLogin: 'Retour à la Connexion',
    freeTrial: 'Essai Gratuit',
    daysRemaining: 'jours restants',
    activeSubscription: 'Abonnement Actif',
    newPlan: 'Nouveau Plan',
    upgradePlan: 'Mettre à Niveau le Plan',
    monthly: 'Mensuel',
    yearly: 'Annuel',
    mostPopular: 'Le Plus Populaire',
    perMonth: '/mois',
    perYear: '/an',
    startPlan: 'Commencer',
    currentPlan: 'Plan Actuel',
    upgradingTo: 'Mise à Niveau Vers',
    upgradeCalculation: 'Calcul de Mise à Niveau',
    amountDueToday: 'Montant Dû Aujourd\'hui',
    creditApplied: 'Crédit Appliqué',
    prorated: 'Au Prorata',
    confirmUpgrade: 'Confirmer la Mise à Niveau',
    starterPlan: 'Plan Démarrage',
    professionalPlan: 'Plan Professionnel',
    perfectForSmallTeams: 'Parfait pour les petites équipes',
    bestForGrowingBusinesses: 'Idéal pour les entreprises en croissance',
    maintenanceWorkOrders: 'Bons de travail de maintenance',
    repairWorkOrders: 'Bons de travail de réparation',
    maintenanceHistoryFeature: 'Historique de maintenance',
    repairHistoryFeature: 'Historique de réparation',
    emailSupport: 'Support par e-mail',
    inventoryTracking: 'Suivi d\'inventaire',
    save: 'Enregistrer',
    allPlansInclude: 'Tous les plans incluent:',
    dayFreeTrial: 'Essai gratuit de 14 jours',
    cancelAnytime: 'Annuler à tout moment',
    securePayments: 'Paiements sécurisés par Stripe',
    support247: 'Support 24/7',
    allPricesCAD: 'Tous les prix sont en dollars canadiens. Les taxes seront calculées à la caisse selon votre localisation.',
    upgradeNowFor: 'Mettre à niveau maintenant pour',
    processing: 'Traitement en cours...',
    daysRemainingCurrentPlan: 'Jours Restants sur le Plan Actuel',
    currentPlanValue: 'Valeur du Plan Actuel (Restant)',
    targetPlanValue: 'Valeur du Plan Cible (Restant)',
    upgradeCostPayNow: 'Coût de Mise à Niveau (Payer Maintenant)',
    upgradeNote: '* Vous ne payez que la différence pour les',
    upgradeNote2: 'jours restants. Votre abonnement expirera à la même date que votre plan actuel.',
    allPlansIncludeText: 'Tous les plans incluent :',
    dayFreeTrialBullet: 'Essai gratuit de 14 jours',
    cancelAnytimeBullet: 'Annuler à tout moment',
    securePaymentsBullet: 'Paiements sécurisés par Stripe',
    support247Bullet: 'Support 24/7',
    pricesInCAD: 'Tous les prix sont en dollars canadiens. Les taxes seront calculées à la caisse en fonction de votre emplacement.',
    detailedMaintenanceInstructions: 'Instructions de maintenance détaillées...',
    addChecklistItem: 'Ajouter un élément de liste (ex: Vérifier niveaux d\'huile, Inspecter courroies)',
    edit: 'Modifier',
    save: 'Enregistrer',
    saving: 'Enregistrement...',
    print: 'Imprimer',
    delete: 'Supprimer',
    details: 'Détails',
    checklist: 'Liste de contrôle',
    files: 'Fichiers',
    of: 'de',
    completedLower: 'terminés',
    filesDocumentation: 'Fichiers et Documentation',
    uploadFile: 'Télécharger un fichier',
    noFilesUploaded: 'Aucun fichier téléchargé',
    uploadFilesDescription: 'Téléchargez des manuels, schémas ou documentation pour ce bon de travail de réparation',
    machineEquipment: 'Machine/Équipement',
    cmmsTagline: 'GMAO facile pour les petites et moyennes usines — suivez le ROI de la maintenance et réparation et réduisez les temps d\'arrêt.',
    companyCode: 'Code d\'entreprise',
    optional: 'optionnel',
    companyCodePlaceholder: 'Entrez le code d\'entreprise pour accéder à une autre entreprise',
    companyCodeHint: 'Laissez vide pour accéder au tableau de bord de votre propre entreprise'
  }
};
